import { useState } from 'react';
import { ListOrdered, AlertTriangle, RotateCcw } from 'lucide-react';
import { useQueueStatus, useErrorHistory, useRetryFailed, useCurrentUser } from '../hooks/useApi';
import { AutoRetryCard } from '../components/queue/AutoRetryCard';
import { DataTable } from '../components/shared/DataTable';
import { StatusBadge } from '../components/shared/StatusBadge';
import { LoadingSpinner } from '../components/shared/LoadingSpinner';
import { ErrorAlert } from '../components/shared/ErrorAlert';
import type { QueueStatusByOrganisation, ErrorHistoryEntry, ErrorStatus } from '../types/api';

type ErrorFilter = 'all' | 'failed';

const STATUS_META: Record<ErrorStatus, { label: string; variant: 'danger' | 'success' | 'warning' }> = {
  failed: { label: 'Blad', variant: 'danger' },
  resolved: { label: 'Naprawiony', variant: 'success' },
  pending: { label: 'W toku', variant: 'warning' },
};

export function QueuePage() {
  const [days, setDays] = useState(7);
  const [failedHours, setFailedHours] = useState(24);
  const [errorFilter, setErrorFilter] = useState<ErrorFilter>('all');

  const currentUser = useCurrentUser();
  const isAdmin = currentUser.data?.isAdmin ?? false;

  const queue = useQueueStatus(days);
  const failed = useErrorHistory(failedHours);
  const retryMutation = useRetryFailed(days);

  const queueColumns = [
    { key: 'organisationName', header: 'Organizacja', sortable: true },
    {
      key: 'queued',
      header: 'W kolejce',
      sortable: true,
      render: (row: QueueStatusByOrganisation) =>
        row.queued > 0 ? <span className="text-amber-600 dark:text-amber-300 font-semibold">{row.queued}</span> : <span className="text-gray-400 dark:text-gray-600">0</span>,
    },
    {
      key: 'dispatched',
      header: 'Wyslane',
      sortable: true,
      render: (row: QueueStatusByOrganisation) =>
        row.dispatched > 0 ? <span className="text-blue-600 dark:text-blue-300">{row.dispatched}</span> : <span className="text-gray-400 dark:text-gray-600">0</span>,
    },
    {
      key: 'processing',
      header: 'Przetwarzane',
      sortable: true,
      render: (row: QueueStatusByOrganisation) =>
        row.processing > 0 ? <StatusBadge label={String(row.processing)} variant="info" pulse /> : <span className="text-gray-400 dark:text-gray-600">0</span>,
    },
    {
      key: 'completed',
      header: 'Zakonczone',
      sortable: true,
      render: (row: QueueStatusByOrganisation) => <span className="text-emerald-600 dark:text-emerald-300">{row.completed}</span>,
    },
    {
      key: 'failed',
      header: 'Bledy',
      sortable: true,
      render: (row: QueueStatusByOrganisation) =>
        row.failed > 0 ? (
          <span className="inline-flex items-center gap-1.5">
            <span className="text-red-600 dark:text-red-300 font-semibold">{row.failed}</span>
            <button
              onClick={() => {
                if (confirm(`Ponowic przetwarzanie ${row.failed} blednych credentiali dla ${row.organisationName}?`)) {
                  retryMutation.mutate(row.organisationId);
                }
              }}
              disabled={retryMutation.isPending}
              className="p-0.5 rounded hover:bg-red-100 dark:hover:bg-red-900/30 text-red-500 dark:text-red-400 transition-colors disabled:opacity-50"
              title="Ponow przetwarzanie"
            >
              <RotateCcw className={`h-3.5 w-3.5 ${retryMutation.isPending ? 'animate-spin' : ''}`} />
            </button>
          </span>
        ) : (
          <span className="text-gray-400 dark:text-gray-600">0</span>
        ),
    },
    { key: 'total', header: 'Razem', sortable: true },
  ];

  const failedColumns = [
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      render: (row: ErrorHistoryEntry) => {
        const meta = STATUS_META[row.status] ?? STATUS_META.failed;
        return (
          <span title={row.resolvedAt ? `Naprawiony: ${new Date(row.resolvedAt).toLocaleString('pl-PL')}` : undefined}>
            <StatusBadge label={meta.label} variant={meta.variant} />
          </span>
        );
      },
    },
    { key: 'organisationName', header: 'Organizacja', sortable: true },
    {
      key: 'credentialId',
      header: 'ID',
      render: (row: ErrorHistoryEntry) => (
        <span className="font-mono text-xs text-gray-500 dark:text-gray-400">{row.credentialId.substring(0, 8)}...</span>
      ),
    },
    {
      key: 'failedAtStep',
      header: 'Krok bledu',
      render: (row: ErrorHistoryEntry) =>
        row.failedAtStep ? <StatusBadge label={row.failedAtStep} variant="danger" /> : <span className="text-gray-400 dark:text-gray-600">-</span>,
    },
    {
      key: 'lastError',
      header: 'Blad',
      render: (row: ErrorHistoryEntry) => (
        <span className="text-xs text-red-500 dark:text-red-300/80 max-w-xs truncate block" title={row.lastError ?? ''}>
          {row.lastError || '-'}
        </span>
      ),
    },
    {
      key: 'occurredAt',
      header: 'Data',
      sortable: true,
      render: (row: ErrorHistoryEntry) =>
        row.occurredAt ? new Date(row.occurredAt).toLocaleString('pl-PL') : '-',
    },
  ];

  const errorRows = (failed.data ?? []).filter((row) => errorFilter === 'all' || row.status === 'failed');
  const unresolvedCount = (failed.data ?? []).filter((row) => row.status === 'failed').length;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-3">
            <ListOrdered className="h-6 w-6 text-brand-600 dark:text-brand-400" />
            Kolejka wystawiania
          </h1>
          <p className="text-sm text-gray-500 mt-1">Status przetwarzania zadan wystawienia</p>
        </div>
        <select
          value={days}
          onChange={(e) => setDays(Number(e.target.value))}
          className="input-field text-sm"
        >
          <option value={1}>Ostatni dzien</option>
          <option value={7}>Ostatnie 7 dni</option>
          <option value={30}>Ostatnie 30 dni</option>
          <option value={90}>Ostatnie 90 dni</option>
          <option value={365}>Ostatni rok</option>
        </select>
      </div>

      {/* Queue Status */}
      <div className="card">
        <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4">Status kolejki per organizacja</h2>
        {queue.isLoading ? (
          <LoadingSpinner size="sm" />
        ) : queue.error ? (
          <ErrorAlert message={queue.error.message} onRetry={() => queue.refetch()} />
        ) : queue.data ? (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-4">
              <div className="bg-gray-100 dark:bg-gray-800/50 rounded-lg p-3 text-center">
                <p className="text-xs text-gray-500">W kolejce</p>
                <p className="text-xl font-bold text-amber-600 dark:text-amber-300">{queue.data.totalQueued}</p>
              </div>
              <div className="bg-gray-100 dark:bg-gray-800/50 rounded-lg p-3 text-center">
                <p className="text-xs text-gray-500">Wyslane</p>
                <p className="text-xl font-bold text-blue-600 dark:text-blue-300">{queue.data.totalDispatched}</p>
              </div>
              <div className="bg-gray-100 dark:bg-gray-800/50 rounded-lg p-3 text-center">
                <p className="text-xs text-gray-500">Przetwarzane</p>
                <p className="text-xl font-bold text-blue-500 dark:text-blue-400">{queue.data.totalProcessing}</p>
              </div>
              <div className="bg-gray-100 dark:bg-gray-800/50 rounded-lg p-3 text-center">
                <p className="text-xs text-gray-500">Zakonczone</p>
                <p className="text-xl font-bold text-emerald-600 dark:text-emerald-300">{queue.data.totalCompleted}</p>
              </div>
              <div className="bg-gray-100 dark:bg-gray-800/50 rounded-lg p-3 text-center">
                <p className="text-xs text-gray-500">Bledy trwale</p>
                <p className={`text-xl font-bold ${queue.data.totalFailed > 0 ? 'text-red-600 dark:text-red-300' : 'text-gray-400 dark:text-gray-600'}`}>{queue.data.totalFailed}</p>
              </div>
            </div>
            <DataTable
              columns={queueColumns}
              data={queue.data.byOrganisation}
              compact
            />
          </>
        ) : null}
      </div>

      {/* Auto-retry canary */}
      <AutoRetryCard isAdmin={isAdmin} />

      {/* Error history — entries survive the fix, so a resolved failure keeps its reason */}
      <div className="card">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
          <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-red-400" />
            Historia bledow
            {unresolvedCount > 0 && (
              <span className="badge-danger">{unresolvedCount} nienaprawionych</span>
            )}
          </h2>
          <div className="flex items-center gap-2">
            <select
              value={errorFilter}
              onChange={(e) => setErrorFilter(e.target.value as ErrorFilter)}
              className="input-field text-xs"
            >
              <option value="all">Wszystkie</option>
              <option value="failed">Tylko nienaprawione</option>
            </select>
            <select
              value={failedHours}
              onChange={(e) => setFailedHours(Number(e.target.value))}
              className="input-field text-xs"
            >
              <option value={6}>Ostatnie 6h</option>
              <option value={24}>Ostatnie 24h</option>
              <option value={72}>Ostatnie 72h</option>
              <option value={168}>Ostatnie 7 dni</option>
              <option value={720}>Ostatnie 30 dni</option>
            </select>
          </div>
        </div>
        <p className="text-xs text-gray-500 mb-3">
          Bledy zostaja w historii takze po naprawie — wpis zmienia status na „Naprawiony", ale powod bledu jest zachowany.
        </p>
        {failed.isLoading ? (
          <LoadingSpinner size="sm" />
        ) : failed.error ? (
          <ErrorAlert message={failed.error.message} onRetry={() => failed.refetch()} />
        ) : (
          <DataTable
            columns={failedColumns}
            data={errorRows}
            compact
            emptyMessage={
              errorFilter === 'failed'
                ? 'Brak nienaprawionych bledow'
                : 'Brak bledow w tym okresie'
            }
          />
        )}
      </div>
    </div>
  );
}
